/**
 * Historique des versions d'une entree (« Item history » de 1Password, « Record history » de Keeper) : chaque
 * enregistrement qui change le CONTENU de l'entree garde la version remplacee, que l'on peut restaurer -- une note
 * ecrasee, une carte mal corrigee, une fusion de doublons regrettee se rattrapent.
 *
 * Les versions vivent UNIQUEMENT dans les secrets chiffres (`secrets.itemHistory`), jamais en metadonnee : elles se
 * synchronisent et s'exportent comme le reste de l'entree, ciphertext-only. Une version ne garde que le contenu ;
 * elle n'emporte jamais :
 *   - les passkeys : retirer une passkey efface sa cle privee du coffre, une version ne la ressusciterait pas ;
 *   - les historiques (mots de passe, versions) et la date du mot de passe courant : pas de version dans une version ;
 *   - les drapeaux d'etat (archive, corbeille, exclusion de la Sante, re-demande du mot de passe maitre) : restaurer
 *     une version ne desarchive rien et ne leve JAMAIS une re-demande posee depuis ;
 *   - les elements lies (`entry-links.ts`) : un lien relie deux entrees, il n'est pas le contenu de l'une d'elles.
 *
 * Module pur, zero E/S : `VaultStore.updateEntrySecrets` le compose a chaque modification.
 */
import type { EntryCustomField, EntrySecrets } from '@klavox/crypto-core';
import { checkboxLabel, isCheckboxField, isMultilineCustomValue, readCustomFields } from './custom-fields.js';
import { readTags } from './entry-tags.js';
import { TYPED_FIELD_GROUPS, fieldSpecs, readTypedFields } from './typed-fields.js';

/** Versions conservees par entree (les plus anciennes sont abandonnees au-dela). */
export const MAX_ITEM_VERSIONS = 10;

/** Cles jamais gardees dans une version, et toujours reprises de l'entree courante a la restauration. */
const CURRENT_ONLY_KEYS = [
  'passkeys',
  'passwordHistory',
  'passwordChangedAt',
  'passwordExpiry',
  'passwordRenewDays',
  'itemHistory',
  'archived',
  'trashedAt',
  'healthExcluded',
  'healthIgnored',
  'passkeyElsewhere',
  'reprompt',
  'links',
] as const;

export interface ItemVersion {
  /** Instant (ms) ou cette version a cesse d'etre le contenu courant. */
  readonly replacedAt: number;
  /** Contenu de l'entree a cet instant, sans passkeys, historiques ni drapeaux d'etat. */
  readonly secrets: EntrySecrets;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Contenu versionne d'une entree : tout, moins ce qui n'appartient qu'a l'entree courante. */
export function itemSnapshot(secrets: EntrySecrets): EntrySecrets {
  const copy: Record<string, unknown> = { ...secrets };
  for (const key of CURRENT_ONLY_KEYS) delete copy[key];
  return copy as EntrySecrets;
}

/** Champ vide : absent, chaine vide, liste ou objet vides -- l'editeur qui le recopie ne change rien. */
function isBlank(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (isPlainObject(value) && Object.values(value).every(isBlank))
  );
}

/**
 * Serialisation a cles triees, champs vides omis : deux contenus egaux donnent la meme chaine, quel que soit l'ordre
 * des cles -- enregistrer sans rien changer ne cree pas de version.
 */
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (isPlainObject(value)) {
    return `{${Object.keys(value)
      .filter((key) => !isBlank(value[key]))
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

function sameValue(a: unknown, b: unknown): boolean {
  return isBlank(a) ? isBlank(b) : canonical(a) === canonical(b);
}

/** Versions d'une entree, plus recente d'abord ; les elements mal formes (serveur, import) sont ignores. */
export function readItemHistory(secrets: EntrySecrets): ItemVersion[] {
  const raw = secrets['itemHistory'];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (item): item is ItemVersion =>
        isPlainObject(item) && Number.isSafeInteger(item['replacedAt']) && (item['replacedAt'] as number) >= 0 && isPlainObject(item['secrets']),
    )
    .map(({ replacedAt, secrets }) => ({ replacedAt, secrets: itemSnapshot(secrets) }))
    .sort((a, b) => b.replacedAt - a.replacedAt)
    .slice(0, MAX_ITEM_VERSIONS);
}

/**
 * Secrets a enregistrer lors d'une modification : les versions sont reprises de `next` s'il les fournit explicitement
 * (effacement, editeur qui recopie l'entree), sinon de `previous` ; le contenu remplace y est ajoute s'il a change. Un
 * changement qui ne touche que les passkeys, les historiques ou un drapeau d'etat ne cree pas de version.
 */
export function withItemHistory(previous: EntrySecrets, next: EntrySecrets, now: number): EntrySecrets {
  const base = readItemHistory(Array.isArray(next['itemHistory']) ? next : previous);
  const replaced = itemSnapshot(previous);
  const changed = !sameValue(replaced, itemSnapshot(next));
  const history = (changed ? [{ replacedAt: now, secrets: replaced }, ...base] : base).slice(0, MAX_ITEM_VERSIONS);
  const { itemHistory: _dropped, ...rest } = next;
  return history.length > 0 ? { ...rest, itemHistory: history } : rest;
}

/**
 * Secrets de l'entree une fois la version restauree : le contenu de la version, et de l'entree courante ses passkeys,
 * ses historiques et ses drapeaux d'etat. Enregistree par `updateEntrySecrets`, la restauration verse le contenu
 * courant dans les versions : elle se defait comme elle s'est faite.
 */
export function restoreItemVersion(current: EntrySecrets, version: ItemVersion): EntrySecrets {
  const kept: Record<string, unknown> = {};
  for (const key of CURRENT_ONLY_KEYS) {
    if (current[key] !== undefined) kept[key] = current[key];
  }
  return { ...itemSnapshot(version.secrets), ...kept } as EntrySecrets;
}

/** Libelles des champs dont on dit qu'ils ont change ; le reste est regroupe sous « Autres champs ». */
const FIELD_LABELS: ReadonlyArray<readonly [string, string]> = [
  ['name', 'Nom'],
  ['username', 'Identifiant'],
  ['password', 'Mot de passe'],
  ['totpSecret', 'Graine 2FA'],
  ['uris', 'Sites'],
  ['uriMatch', 'Detection des sites'],
  ['equivalentDomains', 'Domaines equivalents'],
  ['notes', 'Notes'],
  ['customFields', 'Champs personnalises'],
  ['tags', 'Etiquettes'],
  ['card', 'Carte bancaire'],
  ['identity', 'Identite'],
  ['wifi', 'Reseau Wi-Fi'],
  ['bank', 'Compte bancaire'],
  ['wallet', 'Portefeuille'],
  ['database', 'Base de donnees'],
  ['api-key', 'Cle d’API'],
  ['software-license', 'Licence'],
  ['server', 'Serveur'],
  ['email', 'Compte de messagerie'],
  ['membership', 'Adhesion'],
  ['medical', 'Dossier medical'],
  ['privateKey', 'Cle SSH'],
  ['publicKey', 'Cle SSH'],
];

/**
 * Ce qu'une version a de different du contenu qui l'a remplacee, en libelles lisibles et sans aucune valeur : la
 * liste des versions dit « Mot de passe, Notes », jamais ce qu'ils contenaient.
 */
export function itemVersionChanges(version: EntrySecrets, replacedBy: EntrySecrets): string[] {
  const older = itemSnapshot(version) as Record<string, unknown>;
  const newer = itemSnapshot(replacedBy) as Record<string, unknown>;
  const known = new Set(FIELD_LABELS.map(([key]) => key));
  const labels: string[] = [];
  for (const [key, label] of FIELD_LABELS) {
    if (!sameValue(older[key], newer[key]) && !labels.includes(label)) labels.push(label);
  }
  const keys = new Set([...Object.keys(older), ...Object.keys(newer)]);
  if ([...keys].some((key) => !known.has(key) && !sameValue(older[key], newer[key]))) labels.push('Autres champs');
  return labels;
}

/** Champ d'une version, tel que la fiche de la version le montre. */
export interface ItemVersionField {
  /** Reference de copie (`entry-copy.ts`) DANS la version ; `null` : le champ ne se copie pas. */
  readonly ref: string | null;
  readonly label: string;
  /** Valeur dans cette version ; vide : le champ etait vide, et ne l'est plus depuis. */
  readonly value: string;
  /**
   * Valeur dans le contenu compare : celui qui a remplace la version, ou celui d'aujourd'hui (« Comparer a
   * aujourd'hui ») ; vide : le champ y est vide. Un secret, comme `value`, tant que `sensitive`.
   */
  readonly after: string;
  /** Masque tant que l'entree l'est (mot de passe, graine 2FA, numero de carte, champ cache, cle privee…). */
  readonly sensitive: boolean;
  readonly multiline: boolean;
  /** Differe du contenu compare (`after`), ou champ personnalise renomme depuis. */
  readonly changed: boolean;
}

/**
 * Contenu complet d'une version, champ par champ (« Item history » de 1Password, « Record history » de Keeper) : tout
 * champ renseigne dans la version, plus tout champ vide alors et renseigne depuis -- on voit ce qui a ete ajoute
 * comme ce qui a ete retire. `replacedBy` est le contenu compare : celui qui a remplace la version, ou celui
 * d'aujourd'hui pour voir, avant de restaurer, exactement ce que la restauration changerait. Les valeurs sont lues comme donnees non fiables (chaines seulement) ; les references de
 * copie sont celles d'`entryFieldValue`, relues dans la version au moment du clic.
 */
export function itemVersionFields(version: EntrySecrets, replacedBy: EntrySecrets): ItemVersionField[] {
  const older = itemSnapshot(version);
  const newer = itemSnapshot(replacedBy);
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  const fields: ItemVersionField[] = [];
  const push = (ref: string | null, label: string, before: string, after: string, options: { sensitive?: boolean; multiline?: boolean; renamed?: boolean } = {}): void => {
    if (before.length === 0 && after.length === 0) return;
    fields.push({
      ref: before.length > 0 ? ref : null,
      label,
      value: before,
      after,
      sensitive: options.sensitive === true,
      multiline: options.multiline === true,
      changed: before !== after || options.renamed === true,
    });
  };
  push(null, 'Nom', text(older.name), text(newer.name));
  push('username', 'Identifiant', text(older.username), text(newer.username));
  push('password', 'Mot de passe', text(older.password), text(newer.password), { sensitive: true });
  push(null, 'Graine 2FA', text(older.totpSecret), text(newer.totpSecret), { sensitive: true });
  const olderUris: readonly unknown[] = Array.isArray(older.uris) ? older.uris : [];
  const newerUris: readonly unknown[] = Array.isArray(newer.uris) ? newer.uris : [];
  for (let index = 0; index < Math.max(olderUris.length, newerUris.length); index += 1) {
    push(`uri:${index}`, `Site ${index + 1}`, text(olderUris[index]), text(newerUris[index]));
  }
  for (const group of TYPED_FIELD_GROUPS) {
    const before = readTypedFields(older, group);
    const after = readTypedFields(newer, group);
    for (const spec of fieldSpecs(group)) {
      // Liste fermee : le libelle de l'option (« WPA2 Personnel »), pas sa valeur stockee.
      const shown = (raw: string | undefined): string => spec.options?.find((option) => option.value === raw)?.label ?? raw ?? '';
      push(`typed:${group}:${spec.key}`, spec.label, shown(before[spec.key]), shown(after[spec.key]), { sensitive: spec.sensitive, multiline: spec.multiline });
    }
  }
  const olderCustom = readCustomFields(older);
  const newerCustom = readCustomFields(newer);
  for (let index = 0; index < Math.max(olderCustom.length, newerCustom.length); index += 1) {
    const before = olderCustom[index];
    const after = newerCustom[index];
    // Case a cocher : son etat en mots (« Cochee »), comme la fiche, pas la valeur stockee `true` ; rien a copier.
    const shown = (field: EntryCustomField | undefined): string => (field === undefined ? '' : isCheckboxField(field) ? checkboxLabel(field.value) : field.value);
    const checkbox = [before, after].some((field) => field !== undefined && isCheckboxField(field));
    push(checkbox ? null : `custom:${index}`, before?.name ?? after?.name ?? '', shown(before), shown(after), {
      // Cache d'un cote ou de l'autre : masque. Un champ demasque depuis ne devoile pas son ancienne valeur.
      sensitive: before?.hidden === true || after?.hidden === true,
      // Multiligne d'un cote ou de l'autre (`\r` seul compris, KeePass sous Mac OS classique) : la comparaison garde les lignes.
      multiline: isMultilineCustomValue(before?.value ?? '') || isMultilineCustomValue(after?.value ?? ''),
      renamed: before !== undefined && after !== undefined && before.name !== after.name,
    });
  }
  push(null, 'Cle privee SSH', text(older['privateKey']), text(newer['privateKey']), { sensitive: true, multiline: true });
  push(null, 'Cle publique SSH', text(older['publicKey']), text(newer['publicKey']), { multiline: true });
  push(null, 'Etiquettes', readTags(older).join(', '), readTags(newer).join(', '));
  push(null, 'Notes', text(older.notes), text(newer.notes), { multiline: true });
  return fields;
}
